/**
 * Soft info banner on the dashboard overview.
 * Rotates through tips (incl. pricing season) on each page load.
 */

import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Alert, Button, Space, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";

const { Text } = Typography;

const BannerContainer = styled.div`
  margin-bottom: 24px;
  border-radius: 8px;
  overflow: hidden;
`;

const STORAGE_KEY = "dashboard.infoBanner.rotateIndex";

/** Static soft tips shown alongside season briefing. */
export const STATIC_DASHBOARD_INFO_TIPS = Object.freeze([
  {
    id: "ops-followups",
    type: "info",
    message: "Prioritize today's follow-ups",
    description:
      "Clear overdue callbacks early — stalled leads lose conversion after the first day.",
    href: "/followups",
    hrefLabel: "Follow-ups",
  },
  {
    id: "ops-quotes",
    type: "info",
    message: "Quotes waiting on customers",
    description:
      "Check pending / negotiating quotes and nudge before they expire.",
    href: "/quotes",
    hrefLabel: "Quotes",
  },
  {
    id: "ops-rate-cards",
    type: "info",
    message: "Keep rate cards current",
    description:
      "Seasonal peaks need matching catalogue rows — review Shared Load and Exclusive LD rates.",
    href: "/pricing/rate-cards",
    hrefLabel: "Rate cards",
  },
]);

/**
 * @param {{ seasonBriefing?: object|null }} input
 * @returns {object[]}
 */
export function buildDashboardInfoTips({ seasonBriefing } = {}) {
  /** @type {object[]} */
  const tips = [];

  if (seasonBriefing?.headline) {
    const upcomingSoon =
      seasonBriefing.upcoming &&
      Number(seasonBriefing.upcoming.daysUntil) >= 0 &&
      Number(seasonBriefing.upcoming.daysUntil) <= 21;

    tips.push({
      id: "season-brief",
      type: upcomingSoon ? "warning" : "info",
      message: seasonBriefing.headline,
      description: seasonBriefing.prepNote,
      href: "/pricing/rate-cards",
      hrefLabel: "Season & rates",
      tag: seasonBriefing.automationEnabled ? "Season live" : "Season calendar",
    });

    if (
      seasonBriefing.upcoming?.label &&
      Number(seasonBriefing.upcoming.daysUntil) > 21
    ) {
      tips.push({
        id: "season-upcoming",
        type: "info",
        message: `Next season: ${seasonBriefing.upcoming.label}`,
        description:
          `Starts ${seasonBriefing.upcoming.startsOn} (${seasonBriefing.upcoming.daysUntil}d). ${seasonBriefing.upcoming.tip || ""}`.trim(),
        href: "/pricing/rate-cards",
        hrefLabel: "Season & rates",
        tag: "Coming up",
      });
    }
  }

  tips.push(...STATIC_DASHBOARD_INFO_TIPS);
  return tips;
}

/**
 * Advance rotation index once per page load (session).
 * @param {number} [tipCountHint=1]
 * @returns {number}
 */
export function advanceRotatingTipCursor(tipCountHint = 1) {
  const modulus = Math.max(1, Number(tipCountHint) || 1);
  let next = 0;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const prev = raw != null ? Number(raw) : -1;
    next = (Number.isFinite(prev) ? prev + 1 : 0) % modulus;
    sessionStorage.setItem(STORAGE_KEY, String(next));
  } catch {
    next = Math.floor(Math.random() * modulus);
  }
  return next;
}

/** @deprecated prefer advanceRotatingTipCursor */
export function pickRotatingTipIndex(tipCount) {
  return advanceRotatingTipCursor(tipCount);
}

/**
 * @param {{ seasonBriefing?: object|null }} props
 */
export default function DashboardInfoBanner({ seasonBriefing = null }) {
  const tips = useMemo(
    () => buildDashboardInfoTips({ seasonBriefing }),
    [seasonBriefing]
  );

  // Advance once per mount so async tip-pool growth does not skip slots.
  const [rotateCursor] = useState(() => advanceRotatingTipCursor(8));

  const tip = tips.length > 0 ? tips[rotateCursor % tips.length] : null;
  if (!tip) return null;

  return (
    <BannerContainer data-testid="dashboard-info-banner">
      <Alert
        type={tip.type || "info"}
        showIcon
        icon={<InfoCircleOutlined />}
        message={
          <Space wrap size="small">
            <Text strong>{tip.message}</Text>
            {tip.tag ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                · {tip.tag}
              </Text>
            ) : null}
          </Space>
        }
        description={
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {tip.description}
            </Text>
            {tip.href ? (
              <Link to={tip.href}>
                <Button size="small" type="link" style={{ padding: 0 }}>
                  {tip.hrefLabel || "Open"}
                </Button>
              </Link>
            ) : null}
          </Space>
        }
      />
    </BannerContainer>
  );
}
